import * as React from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

import { cn } from "../utils";
import {
  NOTICE_ICON_CLASS,
  NOTICE_INLINE_TEXT_CLASS,
  noticeRole,
  noticeVariants,
  type NoticeAppearance,
  type NoticeTone,
} from "./notice-variants";

/**
 * Canonical inline messaging primitive: errors, warnings, successes and
 * informational callouts. Use this instead of hand-rolling a `<p
 * className="text-destructive">` or a bordered `<div>` with an icon.
 *
 * Two appearances:
 * - `banner` (default) — a tinted, bordered surface. Use whenever the message
 *   has a title, an action, or runs longer than one line.
 * - `inline` — icon + one short sentence, no surface. Use for field-level
 *   errors and row-level warnings sitting directly under the thing they
 *   describe.
 *
 * The icon carries the tone. Titles render in `text-foreground` and bodies in
 * `text-foreground/80` — a neutral secondary grey — so a long error message
 * never becomes a wall of red.
 */

const DEFAULT_ICONS: Record<NoticeTone, React.ComponentType<{ className?: string }>> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertCircle,
  neutral: Info,
};

export interface NoticeProps extends Omit<React.ComponentProps<"div">, "title" | "role"> {
  /** Semantic tone. Drives the icon, the surface tint and the ARIA role. */
  tone?: NoticeTone;
  /** `banner` for anything with a title/action/multiple lines, `inline` for a single sentence. */
  appearance?: NoticeAppearance;
  /** Short, specific headline. Omit for single-sentence notices. */
  title?: React.ReactNode;
  /**
   * Override the tone's default icon, or pass `null` to render no icon.
   * Prefer the default — a consistent icon per tone is the point of this component.
   */
  icon?: React.ComponentType<{ className?: string }> | null;
  /** Buttons or links that resolve the notice. Floated top-right in the title bar. */
  actions?: React.ReactNode;
  /** When provided, renders a dismiss button in the top-right. */
  onDismiss?: () => void;
  /** Accessible label for the dismiss button. */
  dismissLabel?: string;
  /**
   * Announce this notice to screen readers when it appears. Opt-in, because
   * most notices render with the page as static copy — and a list of them
   * (one per validation issue, per node, per row) would interrupt the user
   * once per item, every time the list re-renders.
   *
   * Set it when the message appears *in response to something the user just
   * did* — a failed save, a submit error, a background job finishing. Errors
   * and warnings then announce assertively, everything else politely.
   */
  live?: boolean;
  /** Escape hatch when `live` picks the wrong urgency. Overrides `live`. */
  role?: "alert" | "status" | "none";
}

function Notice({
  tone = "info",
  appearance = "banner",
  title,
  icon,
  actions,
  onDismiss,
  dismissLabel = "Dismiss",
  live = false,
  role,
  className,
  children,
  ...props
}: NoticeProps) {
  const Icon = icon === null ? null : (icon ?? DEFAULT_ICONS[tone]);
  const isInline = appearance === "inline";

  const titleNode = title ? (
    <div
      data-slot="notice-title"
      className="text-foreground min-w-0 grow basis-48 font-medium tracking-tight"
    >
      {title}
    </div>
  ) : null;

  const descriptionNode = children ? (
    <div
      data-slot="notice-description"
      className={cn(
        // A real basis, so in a narrow container the actions wrap below instead of crushing this.
        "min-w-0 grow basis-48",
        // `text-muted-foreground` (#737373) only reaches ~4.35:1 against
        // the tinted banner surfaces — just under WCAG AA. `foreground/80`
        // reads as the same secondary grey but clears the threshold.
        isInline ? NOTICE_INLINE_TEXT_CLASS[tone] : "text-foreground/80",
        title && "mt-1",
        "[&_a]:underline [&_a]:underline-offset-2 [&_p]:leading-relaxed",
      )}
    >
      {children}
    </div>
  ) : null;

  // Actions live in the title bar, top-right. `-my-1` lets a `size="sm"`
  // button share the title's line without pushing the row taller.
  const actionsNode = actions ? (
    <div
      data-slot="notice-actions"
      className="-my-1 ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2"
    >
      {actions}
    </div>
  ) : null;

  // Without a title the message itself sits beside the icon, with the actions
  // floated to its right — a callout is a title bar plus one message, always.
  const lead = titleNode ?? descriptionNode;

  return (
    <div
      data-slot="notice"
      data-tone={tone}
      data-appearance={appearance}
      role={role ?? (live ? noticeRole(tone) : undefined)}
      className={cn(
        noticeVariants({ tone, appearance }),
        "flex items-start gap-3",
        isInline && "gap-2",
        className,
      )}
      {...props}
    >
      {Icon ? (
        <Icon
          data-slot="notice-icon"
          aria-hidden="true"
          className={cn(
            "size-4 shrink-0 translate-y-0.5",
            isInline && "size-3.5",
            NOTICE_ICON_CLASS[tone],
          )}
        />
      ) : null}

      <div data-slot="notice-content" className="min-w-0 flex-1">
        {actionsNode ? (
          <div
            data-slot="notice-header"
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2"
          >
            {lead}
            {actionsNode}
          </div>
        ) : (
          lead
        )}
        {titleNode ? descriptionNode : null}
      </div>

      {onDismiss ? (
        <button
          type="button"
          data-slot="notice-dismiss"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -m-1 shrink-0 rounded-sm p-1 transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      ) : null}
    </div>
  );
}

export { Notice };
